/**
 * Auth Users List Component
 * 
 * Displays Supabase auth users with filtering and management actions.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchAuthUsers,
  createProfileForUser,
  assignUserToTenant,
} from '../api';
import { useToast } from '@/shared/hooks/use-toast';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Badge } from '@/shared/ui/badge';
import { Card, CardContent } from '@/shared/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import {
  Users,
  Loader2,
  Search,
  MoreHorizontal,
  UserPlus,
  Building2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Shield,
} from 'lucide-react';
import { useLanguage } from '@/core/LanguageContext';
import { CreateProfileDialog } from './CreateProfileDialog';
import { AssignTenantDialog } from './AssignTenantDialog';
import type { AuthUser, ProfileFilter } from '../types';

export function AuthUsersList() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Filter state
  const [profileFilter, setProfileFilter] = useState<ProfileFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  // Dialog state
  const [createProfileUser, setCreateProfileUser] = useState<AuthUser | null>(null);
  const [assignTenantUser, setAssignTenantUser] = useState<AuthUser | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['superadmin', 'auth-users', profileFilter, searchTerm, page],
    queryFn: () =>
      fetchAuthUsers({
        profileFilter,
        search: searchTerm,
        page,
        limit: 50,
      }),
  });

  const createProfileMutation = useMutation({
    mutationFn: (data: { userId: string; email: string; fullName?: string }) =>
      createProfileForUser({
        userId: data.userId,
        email: data.email,
        fullName: data.fullName,
      }),
    onSuccess: () => {
      toast({
        title: t('superadmin.users.profileCreated', 'Profile created successfully'),
      });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'auth-users'] });
      setCreateProfileUser(null);
    },
    onError: (error: Error) => {
      toast({
        title: t('superadmin.users.profileCreateError', 'Failed to create profile'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const assignTenantMutation = useMutation({
    mutationFn: (data: { userId: string; tenantId: string }) =>
      assignUserToTenant({
        userId: data.userId,
        tenantId: data.tenantId,
        status: 'approved',
      }),
    onSuccess: () => {
      toast({
        title: t('superadmin.users.tenantAssigned', 'User assigned to tenant'),
      });
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'auth-users'] });
      setAssignTenantUser(null);
    },
    onError: (error: Error) => {
      toast({
        title: t('superadmin.users.tenantAssignError', 'Failed to assign tenant'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const users = data?.users || [];

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="py-8 text-center">
          <AlertCircle className="h-10 w-10 mx-auto mb-3 text-destructive" />
          <p className="font-medium text-destructive">
            {t('superadmin.users.errorTitle', 'Failed to load users')}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {(error as Error).message}
          </p>
          <Button variant="outline" className="mt-4" onClick={() => refetch()}>
            {t('common.retry', 'Retry')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Users className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">
          {t('superadmin.users.title', 'User Management')}
        </h2>
        {data?.pagination && (
          <Badge variant="secondary">{data.pagination.total}</Badge>
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        {t(
          'superadmin.users.description',
          'Manage Supabase auth users, create profiles for orphaned users, and assign tenants.'
        )}
      </p>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('superadmin.users.searchPlaceholder', 'Search by email...')}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="pl-9"
          />
        </div>
        <Select
          value={profileFilter}
          onValueChange={(value: ProfileFilter) => {
            setProfileFilter(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t('superadmin.users.filterAll', 'All Users')}
            </SelectItem>
            <SelectItem value="with_profile">
              {t('superadmin.users.filterWithProfile', 'With Profile')}
            </SelectItem>
            <SelectItem value="without_profile">
              {t('superadmin.users.filterWithoutProfile', 'Without Profile')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : users.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">
              {t('superadmin.users.empty', 'No users found matching the filters.')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('superadmin.users.colEmail', 'Email')}</TableHead>
                <TableHead>{t('superadmin.users.colProfile', 'Profile')}</TableHead>
                <TableHead>{t('superadmin.users.colTenants', 'Tenants')}</TableHead>
                <TableHead>{t('superadmin.users.colCreated', 'Created')}</TableHead>
                <TableHead>{t('superadmin.users.colLastSignIn', 'Last Sign In')}</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{user.email}</span>
                      {user.profile?.is_super_admin && (
                        <Badge variant="outline" className="text-xs">
                          <Shield className="h-3 w-3 mr-1" />
                          Super
                        </Badge>
                      )}
                    </div>
                    {user.profile?.full_name && (
                      <p className="text-sm text-muted-foreground">{user.profile.full_name}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.profile ? (
                      <Badge variant="default" className="gap-1">
                        <CheckCircle className="h-3 w-3" />
                        {t('superadmin.users.hasProfile', 'Has Profile')}
                      </Badge>
                    ) : (
                      <Badge variant="destructive" className="gap-1">
                        <XCircle className="h-3 w-3" />
                        {t('superadmin.users.noProfile', 'No Profile')}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.tenant_memberships.length === 0 ? (
                      <span className="text-muted-foreground text-sm">
                        {t('superadmin.users.noTenants', 'None')}
                      </span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {user.tenant_memberships.slice(0, 3).map((m) => (
                          <Badge
                            key={m.tenant_id}
                            variant={m.status === 'approved' ? 'secondary' : 'outline'}
                            className="text-xs"
                          >
                            {m.tenant_name}
                          </Badge>
                        ))}
                        {user.tenant_memberships.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{user.tenant_memberships.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(user.created_at)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(user.last_sign_in_at)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!user.profile && (
                          <>
                            <DropdownMenuItem onClick={() => setCreateProfileUser(user)}>
                              <UserPlus className="h-4 w-4 mr-2" />
                              {t('superadmin.users.createProfile', 'Create Profile')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                          </>
                        )}
                        <DropdownMenuItem
                          onClick={() => setAssignTenantUser(user)}
                          disabled={!user.profile}
                        >
                          <Building2 className="h-4 w-4 mr-2" />
                          {t('superadmin.users.assignTenant', 'Assign to Tenant')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {data?.pagination && data.pagination.hasMore && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            {t('common.previous', 'Previous')}
          </Button>
          <span className="px-4 py-2 text-sm text-muted-foreground">
            {t('superadmin.users.page', 'Page')} {page}
          </span>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)}>
            {t('common.next', 'Next')}
          </Button>
        </div>
      )}

      {/* Dialogs */}
      {createProfileUser && (
        <CreateProfileDialog
          open={!!createProfileUser}
          onOpenChange={(open) => !open && setCreateProfileUser(null)}
          userId={createProfileUser.id}
          email={createProfileUser.email}
          onSave={createProfileMutation.mutate}
          isPending={createProfileMutation.isPending}
        />
      )}

      {assignTenantUser && (
        <AssignTenantDialog
          open={!!assignTenantUser}
          onOpenChange={(open) => !open && setAssignTenantUser(null)}
          userId={assignTenantUser.id}
          email={assignTenantUser.email}
          currentMemberships={assignTenantUser.tenant_memberships}
          onSave={assignTenantMutation.mutate}
          isPending={assignTenantMutation.isPending}
        />
      )}
    </div>
  );
}
